import { useEffect, useRef, useState } from 'react';
import { SettingsSection } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { adminApi, type HoldSound } from '@/shared/api/adminApi';
import { apiClient } from '@/shared/api/client';
import { toast } from '@/components/ui/Toast';

const MAX_BYTES = 2 * 1024 * 1024; // 2MB, matching the server validation.
const ACCEPT = 'audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/ogg,.mp3,.wav,.ogg';

/**
 * "צליל המתנה" — upload/preview/delete the music-on-hold clip. The same file is
 * the customer's on-hold music AND the agent's hold-confirmation blip.
 */
export function HoldSoundSection({ initial }: { initial?: HoldSound }) {
  const [state, setState] = useState<HoldSound>(initial ?? { agent_url: null, uploaded: false, filename: null });
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // The preview endpoint requires the auth bearer, so a plain <audio src> would
  // 401. Fetch the bytes through apiClient and play them via an object URL.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!state.uploaded) { setPreviewUrl(null); return; }
    let objectUrl: string | null = null;
    let alive = true;
    void (async () => {
      try {
        const res = await apiClient.get('/admin/settings/hold-sound/preview', { responseType: 'blob' });
        if (!alive) return;
        objectUrl = URL.createObjectURL(res.data as Blob);
        setPreviewUrl(objectUrl);
      } catch {
        if (alive) setPreviewUrl(null);
      }
    })();
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // Re-fetch whenever a new file is uploaded (filename changes) or on mount.
  }, [state.uploaded, state.filename]);

  async function upload(file: File) {
    if (!file.type.startsWith('audio/') && !/\.(mp3|wav|ogg)$/i.test(file.name)) {
      toast('יש להעלות קובץ אודיו (mp3 / wav / ogg)', 'danger');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast('הקובץ גדול מדי — עד 2MB', 'danger');
      return;
    }
    setBusy(true);
    try {
      setState(await adminApi.uploadHoldSound(file));
      toast('צליל ההמתנה הועלה', 'ok');
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה בהעלאת הקובץ', 'danger');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function remove() {
    setBusy(true);
    try {
      setState(await adminApi.deleteHoldSound());
      toast('צליל ההמתנה הוסר', 'ok');
    } catch {
      toast('שגיאה בהסרת הקובץ', 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <SettingsSection
      title="צליל המתנה יחיד"
      icon="mic"
      selfSaving
      badge={state.uploaded ? { label: 'הועלה', tone: 'ok' } : undefined}
      description="הלקוח ישמע את הקובץ כמוזיקת המתנה, והנציג ישמע אותו כאישור בלחיצת 'החזק'. mp3/wav/ogg, עד 2MB."
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }}
        />
        {state.uploaded ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
            <Icon name="mic" size={16} color="var(--accent)" />
            <span style={{ fontSize: 'var(--t-sm)', flex: 1, minWidth: 120 }}>{state.filename ?? 'צליל המתנה'}</span>
            {previewUrl ? (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <audio controls src={previewUrl} style={{ height: 32 }} />
            ) : (
              <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>טוען תצוגה מקדימה…</span>
            )}
            <button type="button" className="btn" disabled={busy} onClick={() => inputRef.current?.click()}>החלף</button>
            <button type="button" className="btn danger" disabled={busy} onClick={remove}>הסר</button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button type="button" className="btn" disabled={busy} onClick={() => inputRef.current?.click()}>
              <Icon name="upload" size={14} /> {busy ? 'מעלה…' : 'העלה קובץ'}
            </button>
            <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>לא הועלה צליל — יופעל צליל ברירת מחדל.</span>
          </div>
        )}
      </div>
    </SettingsSection>
  );
}
